import { initialState } from './constants';
import { normalizeWeeklyCheckinPreferences } from '@/lib/checkin/preferences';
import type { AppState } from './types';
import type { FeatureAccessLevel, FeatureFlag, Referral, ProSource, SubscriptionStatus, SubscriptionTier } from '@/types';
import { supportsFeatureAccessLevel } from '@/lib/feature-flags';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
    'free',
    'monthly',
    'yearly',
    'lifetime',
    'donation',
    'referral_credit',
];

const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = [
    'active',
    'canceled',
    'past_due',
    'unpaid',
    'trialing',
    'incomplete',
    'incomplete_expired',
    'none',
];

const PRO_SOURCES: ProSource[] = [
    'stripe',
    'referral_reward',
    'admin_grant',
    'donation',
    null,
];

const DEFAULT_SUBSCRIPTION: AppState['subscription'] = {
    status: 'none',
    tier: 'free',
    currentPeriodEnd: null,
    isPro: false,
    proSource: null,
};

const DEFAULT_REFERRALS: AppState['referrals'] = {
    code: null,
    referrals: [],
    totalReferrals: 0,
    monthsEarned: 0,
};

function toSubscriptionTier(value: unknown, fallback: SubscriptionTier): SubscriptionTier {
    return typeof value === 'string' && SUBSCRIPTION_TIERS.includes(value as SubscriptionTier)
        ? (value as SubscriptionTier)
        : fallback;
}

function toSubscriptionStatus(value: unknown, fallback: SubscriptionStatus): SubscriptionStatus {
    return typeof value === 'string' && SUBSCRIPTION_STATUSES.includes(value as SubscriptionStatus)
        ? (value as SubscriptionStatus)
        : fallback;
}

function toProSource(value: unknown, fallback: ProSource): ProSource {
    if (value === null) return null;
    return typeof value === 'string' && PRO_SOURCES.includes(value as ProSource)
        ? (value as ProSource)
        : fallback;
}

const ACCESS_LEVELS: FeatureAccessLevel[] = ['disabled', 'admin_only', 'enabled'];

function toFeatureFlag(value: unknown, keyFallback: string): FeatureFlag | null {
    if (!isRecord(value)) return null;

    const key = typeof value.key === 'string' && value.key.length > 0 ? value.key : keyFallback;
    if (!key) return null;

    const minTierRaw = value.minTier ?? value.min_tier;
    const minTier = typeof minTierRaw === 'string' && SUBSCRIPTION_TIERS.includes(minTierRaw as SubscriptionTier)
        ? (minTierRaw as SubscriptionTier)
        : undefined;

    const accessLevelRaw = value.accessLevel ?? value.access_level;
    const accessLevel = supportsFeatureAccessLevel(key)
        && typeof accessLevelRaw === 'string'
        && ACCESS_LEVELS.includes(accessLevelRaw as FeatureAccessLevel)
        ? (accessLevelRaw as FeatureAccessLevel)
        : undefined;

    return {
        key,
        description: typeof value.description === 'string' ? value.description : undefined,
        isProOnly: typeof value.isProOnly === 'boolean'
            ? value.isProOnly
            : Boolean(value.is_pro_only),
        minTier,
        isEnabled: typeof value.isEnabled === 'boolean'
            ? value.isEnabled
            : (typeof value.is_enabled === 'boolean' ? value.is_enabled : true),
        accessLevel,
    };
}

function toReferral(value: unknown): Referral | null {
    if (!isRecord(value)) return null;
    if (typeof value.id !== 'string' || value.id.length === 0) return null;

    const referredUserId = typeof value.referredUserId === 'string'
        ? value.referredUserId
        : (typeof value.referred_user_id === 'string' ? value.referred_user_id : '');

    const statusRaw = value.status;
    const status = statusRaw === 'converted' ? 'converted' : 'pending';

    const createdAt = typeof value.createdAt === 'string'
        ? value.createdAt
        : (typeof value.created_at === 'string' ? value.created_at : new Date(0).toISOString());

    const rewardClaimedRaw = value.rewardClaimed ?? value.reward_claimed;

    return {
        id: value.id,
        referredUserId,
        status,
        createdAt,
        rewardClaimed: typeof rewardClaimedRaw === 'boolean' ? rewardClaimedRaw : false,
    };
}

export function normalizePreferences(value: unknown): AppState['preferences'] {
    const fallback = initialState.preferences;
    if (!isRecord(value)) {
        return {
            ...fallback,
            weeklyCheckIn: normalizeWeeklyCheckinPreferences(undefined, fallback.weeklyCheckIn),
        };
    }

    const raw = value as Record<string, unknown>;
    const weekStart = raw.weekStart === 'Sunday' ? 'Sunday' : 'Monday';
    const unitSystem = raw.unitSystem === 'imperial' ? 'imperial' : 'metric';
    const dateFormat = raw.dateFormat === 'MM/DD/YYYY' || raw.dateFormat === 'YYYY-MM-DD'
        ? raw.dateFormat
        : 'DD/MM/YYYY';
    const timeFormat = raw.timeFormat === '12h' ? '12h' : '24h';

    return {
        ...fallback,
        ...raw,
        proMode: typeof raw.proMode === 'boolean' ? raw.proMode : fallback.proMode,
        darkMode: typeof raw.darkMode === 'boolean' ? raw.darkMode : fallback.darkMode,
        unitSystem,
        weekStart,
        timezone: typeof raw.timezone === 'string' && raw.timezone.length > 0
            ? raw.timezone
            : fallback.timezone,
        dateFormat,
        timeFormat,
        showEducation: typeof raw.showEducation === 'boolean' ? raw.showEducation : fallback.showEducation,
        hasSeenWalkthrough: typeof raw.hasSeenWalkthrough === 'boolean' ? raw.hasSeenWalkthrough : fallback.hasSeenWalkthrough,
        thresholdAckSignature: typeof raw.thresholdAckSignature === 'string' ? raw.thresholdAckSignature : undefined,
        weeklyCheckIn: normalizeWeeklyCheckinPreferences(raw.weeklyCheckIn ?? raw.weekly_check_in, fallback.weeklyCheckIn),
    };
}

export function normalizeFeatureFlags(value: unknown): AppState['featureFlags'] {
    if (!isRecord(value)) return {};

    const normalized: AppState['featureFlags'] = {};
    for (const [key, rawFlag] of Object.entries(value)) {
        const flag = toFeatureFlag(rawFlag, key);
        if (flag) {
            normalized[flag.key] = flag;
        }
    }
    return normalized;
}

export function normalizeSubscription(value: unknown): AppState['subscription'] {
    const fallback = DEFAULT_SUBSCRIPTION;
    if (!isRecord(value)) return { ...fallback };

    const currentPeriodEndRaw = value.currentPeriodEnd ?? value.current_period_end;

    return {
        status: toSubscriptionStatus(value.status, fallback.status),
        tier: toSubscriptionTier(value.tier, fallback.tier),
        currentPeriodEnd: typeof currentPeriodEndRaw === 'string' || currentPeriodEndRaw === null
            ? currentPeriodEndRaw
            : fallback.currentPeriodEnd,
        isPro: typeof value.isPro === 'boolean' ? value.isPro : fallback.isPro,
        proSource: toProSource(value.proSource ?? value.pro_source, fallback.proSource),
    };
}

export function normalizeReferrals(value: unknown): AppState['referrals'] {
    const fallback = DEFAULT_REFERRALS;
    if (!isRecord(value)) return { ...fallback };

    const rawReferrals = Array.isArray(value.referrals) ? value.referrals : [];
    const referrals = rawReferrals.map(toReferral).filter((r): r is Referral => r !== null);

    const totalReferrals = typeof value.totalReferrals === 'number' && Number.isFinite(value.totalReferrals)
        ? value.totalReferrals
        : referrals.length;

    const monthsEarned = typeof value.monthsEarned === 'number' && Number.isFinite(value.monthsEarned)
        ? value.monthsEarned
        : fallback.monthsEarned;

    return {
        code: typeof value.code === 'string' || value.code === null ? value.code : fallback.code,
        referrals,
        totalReferrals,
        monthsEarned,
    };
}
